import { useCallback, useMemo, useReducer } from 'react';
import { emptyLayout } from './templateLayout';

// How far back undo reaches.
const MAX_HISTORY = 100;
// Edits sharing a key this close together are one step to undo — the
// keystrokes of a label, the clicks of a font-size stepper.
const COALESCE_MS = 1000;

const initialState = {
  past: [],
  present: emptyLayout(),
  future: [],
  lastKey: null,
  lastAt: 0,
};

// A reducer rather than state plus refs because StrictMode runs updaters
// twice: everything an edit depends on, coalescing included, has to travel in
// the action and the state for both runs to agree.
const reducer = (state, action) => {
  switch (action.type) {
    case 'reset':
      return { ...initialState, present: action.layout };
    case 'commit': {
      const next = action.update(state.present);
      if (next === state.present) return state;
      const coalesce =
        !!action.key && action.key === state.lastKey && action.at - state.lastAt < COALESCE_MS;
      return {
        past: coalesce ? state.past : [...state.past, state.present].slice(-MAX_HISTORY),
        present: next,
        future: [],
        lastKey: action.key || null,
        lastAt: action.at,
      };
    }
    case 'undo':
      if (state.past.length === 0) return state;
      return {
        past: state.past.slice(0, -1),
        present: state.past[state.past.length - 1],
        future: [state.present, ...state.future],
        lastKey: null,
        lastAt: 0,
      };
    case 'redo':
      if (state.future.length === 0) return state;
      return {
        past: [...state.past, state.present],
        present: state.future[0],
        future: state.future.slice(1),
        lastKey: null,
        lastAt: 0,
      };
    default:
      return state;
  }
};

// The page being built, with undo and redo. Every change goes through
// `commit(update, key)`, where `update` maps the current layout to the next
// (handing back the same layout is a no-op, not a step). A drag commits once,
// when it is dropped, so a step to undo is a whole move rather than a pixel of
// one.
export const useTemplateLayout = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const reset = useCallback((layout) => dispatch({ type: 'reset', layout }), []);
  const commit = useCallback(
    (update, key = null) => dispatch({ type: 'commit', update, key, at: Date.now() }),
    [],
  );
  const undo = useCallback(() => dispatch({ type: 'undo' }), []);
  const redo = useCallback(() => dispatch({ type: 'redo' }), []);

  return useMemo(
    () => ({
      layout: state.present,
      commit,
      reset,
      undo,
      redo,
      canUndo: state.past.length > 0,
      canRedo: state.future.length > 0,
    }),
    [state, commit, reset, undo, redo],
  );
};
